import type { CSSProperties, FC, KeyboardEvent, ReactElement, ReactNode, Ref } from 'react';
import { createElement } from 'react';

import type {
  A11y,
  FlexAlign,
  FlexDirection,
  FlexJustify,
  FlexTag,
  FlexWrap,
} from '@snowit/icetypes';

import testId from '@snowit/utils/testId';
import validators from '@snowit/utils/validators';

import cx from 'classnames';
import s from './Flex.module.scss';

export interface IFlex {
  children: ReactNode;
  tag?: FlexTag;
  direction?: FlexDirection;
  justify?: FlexJustify;
  align?: FlexAlign;
  /** Component  row and column  gap in REM */
  width?: 'full' | 'fit-content';
  gap?: number | number[];
  wrap?: FlexWrap;
  onClick?: (...args: any[]) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  id?: string;
  className?: string;
  style?: CSSProperties;
  ref?: Ref<HTMLElement>;
  a11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
}

const Flex: FC<IFlex> = (props: IFlex): ReactElement => {
  const {
    children,
    tag = 'div',
    direction = 'row',
    justify = 'flex-start',
    align = 'flex-start',
    width = 'full',
    gap = [0],
    wrap = 'nowrap',
    onClick,
    onKeyDown,
    id,
    className,
    style,
    ref,
    a11y,
    test,
  } = props;

  const _gap = (Array.isArray(gap) ? gap : [gap]).filter((g): g is number =>
    validators.isNumber(g),
  );

  const flexProps = {
    ref,
    onClick,
    onKeyDown,
    id,
    className: cx(s.wrapper, className),
    style: {
      ...style,
      '--l-width': width === 'full' ? '100%' : 'fit-content',
      '--l-direction': direction,
      '--l-justify': justify,
      '--l-align': align,
      '--l-row-gap': `${_gap[0]}rem`,
      '--l-column-gap': `${_gap[1] ?? _gap[0]}rem`,
      '--l-wrap': wrap,
    } as CSSProperties,
    role: a11y?.role,
    'aria-label': a11y?.label,
    tabIndex: a11y?.tabIndex,
    ...testId(test),
  };

  return createElement(tag, flexProps, children);
};

export default Flex;
